import React, { createContext, useState, useContext, useEffect } from "react";

type ThemeType = "light" | "dark";

interface ThemeContextProps {
  theme: ThemeType;
  toggleTheme: () => void;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Başlangıç temasını her zaman "light" olarak ata
  const getInitialTheme = (): ThemeType => {
    return "light";
  };

  const [theme, setThemeState] = useState<ThemeType>(() => getInitialTheme());

  useEffect(() => {
    // Tema değiştiğinde localStorage'a kaydet
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", theme);

      // HTML document element'e tema class'ını ekle
      document.documentElement.setAttribute("data-bs-theme", theme);
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(theme);

      // Meta theme-color etiketini güncelle (mobil için)
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute(
          "content",
          theme === "dark" ? "#212529" : "#ffffff"
        );
      }
    }
  }, [theme]);

  // Sistem tercihi değiştiğinde temayı güncelleme (Devre dışı bırakıldı)
  useEffect(() => {
    // Light mode'u zorla
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", "light");
      document.documentElement.setAttribute("data-bs-theme", "light");
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    }
  }, []);

  const toggleTheme = () => {
    setThemeState((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const setTheme = (newTheme: ThemeType) => {
    if (newTheme === "light" || newTheme === "dark") {
      setThemeState(newTheme);
    }
  };

  const value = React.useMemo(
    () => ({
      theme,
      toggleTheme,
      setTheme,
    }),
    [theme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Kolay kullanım için hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme hook must be used within a ThemeProvider");
  }
  return context;
};

// ThemeContext'i de export et (isteğe bağlı)
export { ThemeContext };
export type { ThemeType, ThemeContextProps };
